import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Languages } from '@core/services/language.service';
import { StorageService } from '@core/services/storage.service';
import { ContactDetails } from '@modules/contacts/shared/consts/contact-form.const';
import { AddressAutocompleteConfigV2 } from '@shared/components/address-with-autocomplete-v2/address-with-autocomplete-v2.component';
import { countries } from '@shared/datas/countries';
import { ButtonSize, ButtonTypes } from '@shared/modules/ui/components/button/button.component';

@Component({
  selector: 'contact-details-communication',
  templateUrl: './communication.component.html',
  styleUrls: ['./communication.component.scss']
})
export class CommunicationComponent {
  @Input() isPreview: boolean = false;
  @Input() parentForm: UntypedFormGroup;

  ContactDetails = ContactDetails;
  countries = countries;

  ButtonTypes = ButtonTypes;
  ButtonSize = ButtonSize;

  addressConfig: AddressAutocompleteConfigV2 = {
    addressControlName: ContactDetails.address,
    postalCodeControlName: ContactDetails.postalCode,
    townControlName: ContactDetails.town,
    countryControlName: ContactDetails.country,
    addressLabel: 'Contacts.V2.address',
    addressPlaceholder: 'Contacts.V2.addressPlaceholder',
    townLabel: 'Contacts.V2.town',
    townPlaceholder: 'Contacts.V2.townPlaceholder',
    postalCodeLabel: 'Contacts.V2.postalCode',
    postalCodePlaceholder: 'Contacts.V2.postalCodePlaceholder',
    countryLabel: 'Contacts.V2.country',
    countryPlaceholder: 'Contacts.V2.countryPlaceholder'
  };

  get communications() {
    return this.parentForm.controls[ContactDetails.communications] as UntypedFormArray;
  }

  get links() {
    return this.parentForm.controls[ContactDetails.links] as UntypedFormArray;
  }

  get additionalEmails() {
    return this.parentForm.controls[ContactDetails.additionalEmails] as UntypedFormArray;
  }

  get phone(): string {
    return this.parentForm.get(ContactDetails.phone).value;
  }

  get countryCode(): string {
    return this.parentForm.get(ContactDetails.phoneCountry).value;
  }

  get countryName(): string {
    const countryCode = this.parentForm.get(ContactDetails.country)?.value;
    return countries.find((c) => c.id === countryCode)?.text || '';
  }

  get formattedAddress(): string {
    if (!this.hasAddress()) return '-';

    const street = this.parentForm.get(ContactDetails.address)?.value;
    const postalCode = this.parentForm.get(ContactDetails.postalCode)?.value;
    const town = this.parentForm.get(ContactDetails.town)?.value;

    const cityPart = [postalCode, town].filter(Boolean).join(' ');

    return [street, cityPart, this.countryName].filter(Boolean).join(', ');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private changes: ChangeDetectorRef,
    private storage: StorageService
  ) {}

  addCommunication() {
    const communicationForm = this.fb.group({
      [ContactDetails.phone]: [null],
      [ContactDetails.phoneCountry]: [this.storage.UserPerson.language || Languages.DE]
    });

    this.communications.push(communicationForm);
    this.communications.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
    setTimeout(() => this.changes.detectChanges(), 0);
  }

  deleteCommunication(index: number) {
    this.communications.removeAt(index);
    this.communications.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
  }

  addAdditionalEmail() {
    this.additionalEmails.push(this.createAdditionalEmailForm());
    this.additionalEmails.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
    setTimeout(() => this.changes.detectChanges(), 0);
  }

  deleteAdditionalEmail(index: number) {
    this.additionalEmails.removeAt(index);
    this.additionalEmails.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
  }

  addWebInfo() {
    const linkForm = this.fb.group({
      [ContactDetails.linkName]: [null, [Validators.maxLength(255)]],
      [ContactDetails.linkUrl]: [null, [Validators.maxLength(255)]]
    });

    this.links.push(linkForm);
    this.links.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
    setTimeout(() => this.changes.detectChanges(), 0);
  }

  deleteWebInfo(index: number) {
    this.links.removeAt(index);
    this.links.markAsDirty();
    this.parentForm.markAsDirty();
    this.parentForm.updateValueAndValidity({ emitEvent: true });
  }

  hasAddress(): boolean {
    return (
      this.parentForm.get(ContactDetails.address)?.value ||
      this.parentForm.get(ContactDetails.postalCode)?.value ||
      this.parentForm.get(ContactDetails.town)?.value ||
      this.parentForm.get(ContactDetails.country)?.value
    );
  }

  private createAdditionalEmailForm(email: string | null = null): UntypedFormGroup {
    return this.fb.group({
      [ContactDetails.email]: [email, [Validators.email, Validators.maxLength(255)]]
    });
  }
}
